import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from 'app/components/ui/dialog';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from 'app/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from 'app/components/ui/popover';
import { Check, ChevronsUpDown, Plus, Building2, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { useFormContext } from 'react-hook-form';
import { useSupplierManagementSlice } from 'app/pages/SupplierManagement/slice';
import { Supplier } from 'app/pages/SupplierManagement/slice/types';
import useDebounce from 'utils/hooks/debounce-hook';
import { useToast } from 'app/components/ui/use-toast';
import { Toaster } from 'app/components/ui/toaster';

interface SupplierSelectorProps {
    value?: number;
    onChange: (supplier: Supplier | null) => void;
    placeholder?: string;
    className?: string;
}

export function SupplierSelector({
    value,
    onChange,
    placeholder = 'Search suppliers...',
    className,
}: SupplierSelectorProps) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
        null,
    );
    const [isLoading, setIsLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [shouldResetForm, setShouldResetForm] = useState(false);
    const { toast } = useToast();

    const debouncedSearch = useDebounce(searchTerm, 300);
    const { useSearchSuppliersQuery, useCreateSupplierMutation } =
        useSupplierManagementSlice();
    const [searchSuppliers] = useSearchSuppliersQuery();
    const [createSupplier, { isLoading: isCreating }] =
        useCreateSupplierMutation();

    // Helper function to extract suppliers from response
    const extractSuppliers = (response: any): Supplier[] => {
        return response?.data?.suppliers || [];
    };

    // Load suppliers (initial load or search)
    useEffect(() => {
        const loadSuppliers = async () => {
            setIsLoading(true);
            try {
                const params =
                    debouncedSearch.length >= 2
                        ? { search: debouncedSearch, limit: 20 }
                        : { limit: 20 };

                const response = await searchSuppliers(params).unwrap();
                console.log('API Response:', response); // Debug log
                const suppliers = extractSuppliers(response);
                console.log('Extracted Suppliers:', suppliers); // Debug log
                setSuppliers(suppliers);
            } catch (error) {
                console.error('Error loading suppliers:', error);
                setSuppliers([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadSuppliers();
    }, [debouncedSearch, searchSuppliers]);

    // Initialize selected supplier when value prop changes
    useEffect(() => {
        if (value && suppliers.length > 0 && !selectedSupplier) {
            const foundSupplier = suppliers.find(s => s.supplier_id === value);
            if (foundSupplier) {
                setSelectedSupplier(foundSupplier);
            }
        }
    }, [value, suppliers, selectedSupplier]);

    // Reset the shouldResetForm flag after it's been used
    React.useEffect(() => {
        if (shouldResetForm) {
            const timer = setTimeout(() => {
                setShouldResetForm(false);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [shouldResetForm]);

    // Handle supplier selection
    const handleSelectSupplier = (supplier: Supplier) => {
        setSelectedSupplier(supplier);
        onChange(supplier);
        setOpen(false);
        setSearchTerm('');
    };

    // Handle adding new supplier
    const handleAddSupplier = async (supplierData: Partial<Supplier>) => {
        try {
            const response = await createSupplier(supplierData).unwrap();
            const newSupplier = response.data;
            setSelectedSupplier(newSupplier);
            onChange(newSupplier);
            setShowAddModal(false);
            setOpen(false);
            setShouldResetForm(true); // Trigger form reset

            // Show success toast
            toast({
                title: 'Success',
                description: 'Supplier added successfully',
                variant: 'default',
            });
        } catch (error: any) {
            console.error('Error creating supplier:', error);

            // Extract error message from API response
            let errorMessage = 'Failed to add supplier. Please try again.';
            if (error?.data?.error) {
                errorMessage = error.data.error;
            } else if (error?.data?.message) {
                errorMessage = error.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            // Show error toast
            toast({
                title: 'Error',
                description: errorMessage,
                variant: 'destructive',
            });

            // Don't close the modal on error - let user fix the issue
        }
    };

    // Clear selection
    const handleClearSelection = () => {
        setSelectedSupplier(null);
        onChange(null);
    };

    return (
        <div className={cn('space-y-2', className)}>
            <Label className="text-xs font-medium text-gray-800 dark:text-gray-200 flex items-center">
                <span className="w-1.5 h-1.5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full mr-2"></span>
                Supplier
            </Label>

            <div className="flex items-center gap-2">
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="flex-1 justify-between h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-red-500 focus:border-red-500 rounded-lg transition-all duration-200 text-sm"
                        >
                            {selectedSupplier ? (
                                <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-gray-500" />
                                    <span className="truncate">
                                        {selectedSupplier.supplier_name}
                                    </span>
                                    {selectedSupplier.contact_person && (
                                        <Badge variant="secondary" className="text-xs">
                                            {selectedSupplier.contact_person}
                                        </Badge>
                                    )}
                                </div>
                            ) : (
                                <span className="text-gray-500">{placeholder}</span>
                            )}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                        <Command>
                            <CommandInput
                                placeholder="Search suppliers..."
                                value={searchTerm}
                                onValueChange={setSearchTerm}
                                className="h-9"
                            />
                            <CommandList>
                                <CommandEmpty>
                                    {isLoading ? (
                                        <div className="py-6 text-center text-sm text-gray-500">
                                            {searchTerm.length >= 2 ? 'Searching...' : 'Loading...'}
                                        </div>
                                    ) : searchTerm.length < 2 ? (
                                        <div className="py-6 text-center text-sm text-gray-500">
                                            Type to search suppliers
                                        </div>
                                    ) : (
                                        <div className="py-6 text-center">
                                            <div className="text-sm text-gray-500 mb-2">
                                                No suppliers found
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setShowAddModal(true)}
                                                className="text-xs"
                                            >
                                                <Plus className="h-3 w-3 mr-1" />
                                                Add New Supplier
                                            </Button>
                                        </div>
                                    )}
                                </CommandEmpty>
                                <CommandGroup>
                                    {Array.isArray(suppliers) &&
                                        suppliers.length > 0 &&
                                        suppliers.map(supplier => (
                                            <CommandItem
                                                key={supplier.supplier_id}
                                                value={supplier.supplier_name}
                                                onSelect={() => handleSelectSupplier(supplier)}
                                                className="flex items-center justify-between"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Building2 className="h-4 w-4 text-gray-500" />
                                                    <div>
                                                        <div className="font-medium">
                                                            {supplier.supplier_name}
                                                        </div>
                                                        {supplier.contact_person && (
                                                            <div className="text-xs text-gray-500">
                                                                {supplier.contact_person}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <Check
                                                    className={cn(
                                                        'ml-auto h-4 w-4',
                                                        selectedSupplier?.supplier_id ===
                                                            supplier.supplier_id
                                                            ? 'opacity-100'
                                                            : 'opacity-0',
                                                    )}
                                                />
                                            </CommandItem>
                                        ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>

                {selectedSupplier && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearSelection}
                        className="h-10 w-10 p-0 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                        <X className="h-4 w-4 text-red-500" />
                    </Button>
                )}

                <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                    <DialogTrigger asChild>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-10 px-3 hover:bg-green-50 dark:hover:bg-green-900/20 border-green-200 dark:border-green-800"
                        >
                            <Plus className="h-4 w-4 mr-1" />
                            Add
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Building2 className="h-5 w-5 text-white" />
                                Add New Supplier
                            </DialogTitle>
                        </DialogHeader>
                        <AddSupplierForm
                            onSubmit={handleAddSupplier}
                            isLoading={isCreating}
                            onCancel={() => setShowAddModal(false)}
                            shouldReset={shouldResetForm}
                        />
                    </DialogContent>
                </Dialog>
            </div>

            {selectedSupplier && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-sm">
                        <Building2 className="h-4 w-4 text-green-600" />
                        <span className="font-medium text-green-800 dark:text-green-200">
                            Selected: {selectedSupplier.supplier_name}
                        </span>
                    </div>
                    {selectedSupplier.contact_person && (
                        <div className="text-xs text-green-700 dark:text-green-300 mt-1">
                            Contact: {selectedSupplier.contact_person}
                        </div>
                    )}
                </div>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded">
                Search and select a supplier or add a new one
            </p>

            <Toaster />
        </div>
    );
}

// Add Supplier Form Component
interface AddSupplierFormProps {
    onSubmit: (data: Partial<Supplier>) => void;
    isLoading: boolean;
    onCancel: () => void;
    shouldReset?: boolean;
}

function AddSupplierForm({
    onSubmit,
    isLoading,
    onCancel,
    shouldReset = false,
}: AddSupplierFormProps) {
    const [formData, setFormData] = useState({
        supplier_name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        status: 'active' as const,
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.supplier_name.trim()) {
            onSubmit(formData);
        }
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const resetForm = () => {
        setFormData({
            supplier_name: '',
            contact_person: '',
            email: '',
            phone: '',
            address: '',
            status: 'active' as const,
        });
    };

    // Reset form only when explicitly requested (successful submission)
    React.useEffect(() => {
        if (shouldReset) {
            resetForm();
        }
    }, [shouldReset]);

    return (
        <form onSubmit={handleSubmit} className="space-y-4 px-3">
            <div className="space-y-2">
                <Label htmlFor="supplier_name" className="text-sm font-medium">
                    Supplier Name *
                </Label>
                <Input
                    id="supplier_name"
                    value={formData.supplier_name}
                    onChange={e => handleInputChange('supplier_name', e.target.value)}
                    placeholder="Enter supplier name"
                    required
                    className="h-10"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="contact_person" className="text-sm font-medium">
                    Contact Person
                </Label>
                <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={e => handleInputChange('contact_person', e.target.value)}
                    placeholder="Enter contact person name"
                    className="h-10"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                    Email
                </Label>
                <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    className="h-10"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">
                    Phone
                </Label>
                <Input
                    id="phone"
                    value={formData.phone}
                    onChange={e => handleInputChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                    className="h-10"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="address" className="text-sm font-medium">
                    Address
                </Label>
                <Input
                    id="address"
                    value={formData.address}
                    onChange={e => handleInputChange('address', e.target.value)}
                    placeholder="Enter address"
                    className="h-10"
                />
            </div>

            <div className="flex justify-end gap-2 py-3">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={isLoading || !formData.supplier_name.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                >
                    {isLoading ? 'Adding...' : 'Add Supplier'}
                </Button>
            </div>
        </form>
    );
}
